/**
 * PlannerCalendar — month grid of every scheduled draft.
 *
 *   /portal/content-planner/calendar
 *
 * Combines:
 *   · Local drafts with status='scheduled' and a scheduled_for or
 *     buffer_due_at timestamp.
 *   · Buffer-side scheduled posts (whatever Buffer says is queued).
 *
 * Shows network colour dots per day, click a day to see the posts, and
 * click a post to open it in the lane editor.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useAdminToken, studioFetch } from "../components/studio/_shared";
import ContentPlannerLayout from "../components/layout/ContentPlannerLayout";

const NETWORK_COLOR = {
  instagram:         "#E1306C",
  "linkedin-text":   "#0A66C2",
  "linkedin-carousel": "#0A66C2",
  facebook:          "#1877F2",
  twitter:           "#000000",
  youtube:           "#FF0000",
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function fmtMonth(d) {
  return d.toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
}
function isoDay(d) {
  return d.toISOString().slice(0, 10);
}

export default function PlannerCalendar() {
  const navigate = useNavigate();
  const token = useAdminToken();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (!token) navigate("/admin/login", { replace: true });
  }, [token, navigate]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const d = await studioFetch("/api/content-calendar/drafts", { token });
      // Keep anything with a date in scheduled_for or buffer_due_at
      const rows = (d.drafts || []).filter((r) =>
        r.scheduled_for || r.buffer_due_at,
      );
      setDrafts(rows);
    } catch (e) {
      if (/401|403/.test(e.message)) {
        localStorage.removeItem("hv_admin_token");
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(e.message);
    } finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  /* group by yyyy-mm-dd. Rows with posted_channels=['instagram','linkedin-text','facebook']
     are EXPANDED into one virtual entry per channel — so a single posted launch
     announcement that went out on IG + LinkedIn + FB shows three coloured dots on
     the calendar day instead of one. */
  const byDay = useMemo(() => {
    const out = {};
    for (const d of drafts) {
      const when = d.posted_at || d.buffer_due_at || d.scheduled_for;
      if (!when) continue;
      const key = new Date(when).toISOString().slice(0, 10);
      const channels = (Array.isArray(d.posted_channels) && d.posted_channels.length > 0)
        ? d.posted_channels
        : [d.angle];
      for (const ch of channels) {
        // Synthetic per-channel row. Real DB row id is suffixed so the
        // virtual entries get unique React keys but still link back via
        // source_slug / source_type when opened in the lane editor.
        const synthetic = channels.length === 1
          ? d
          : { ...d, id: `${d.id}::${ch}`, angle: ch, _channel_view: ch };
        (out[key] = out[key] || []).push(synthetic);
      }
    }
    return out;
  }, [drafts]);

  /* build the grid (6 weeks, Monday-first) */
  const cells = useMemo(() => {
    const first = startOfMonth(cursor);
    const startWeekday = (first.getDay() + 6) % 7;     // Mon=0
    const grid = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(first.getFullYear(), first.getMonth(), i - startWeekday + 1);
      grid.push(date);
    }
    return grid;
  }, [cursor]);

  const todayKey = isoDay(new Date());
  const month = cursor.getMonth();

  return (
    <ContentPlannerLayout>
      <Helmet>
        <title>Calendar — HidroVerse Content Planner</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div data-testid="planner-calendar">

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button type="button"
                    onClick={() => setCursor(addMonths(cursor, -1))}
                    data-testid="cal-prev"
                    className="hv-pill">
              <ChevronLeft size={12} /> Prev
            </button>
            <button type="button"
                    onClick={() => setCursor(startOfMonth(new Date()))}
                    data-testid="cal-today"
                    className="hv-pill">
              Today
            </button>
            <button type="button"
                    onClick={() => setCursor(addMonths(cursor, 1))}
                    data-testid="cal-next"
                    className="hv-pill">
              Next <ChevronRight size={12} />
            </button>
            <h2 className="ml-3"
                style={{ fontSize: "1.1rem", fontWeight: 600 }}
                data-testid="cal-month-label">
              {fmtMonth(cursor)}
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {Object.entries(NETWORK_COLOR).slice(0, 4).map(([k, c]) => (
              <span key={k} className="font-mono uppercase"
                    style={{ fontSize: "0.58rem", letterSpacing: "0.16em",
                             color: c, fontWeight: 700 }}>
                ● {k.replace("linkedin-text", "linkedin")}
              </span>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-3 px-3 py-2"
               style={{ background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#B85C38", fontSize: "0.82rem",
                        borderRadius: 8 }}
               data-testid="cal-error">
            {error}
          </div>
        )}

        {/* grid */}
        <div className="hv-card-soft" style={{ padding: 12 }}
             data-testid="cal-grid">
          <div className="grid"
               style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
            {DAY_LABELS.map((d) => (
              <div key={d}
                   className="font-mono uppercase text-center"
                   style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                            color: "rgba(12,42,64,0.55)", fontWeight: 700,
                            padding: "6px 0" }}>
                {d}
              </div>
            ))}
            {cells.map((date) => {
              const key = isoDay(date);
              const inMonth = date.getMonth() === month;
              const isToday = key === todayKey;
              const posts = byDay[key] || [];
              return (
                <button key={key} type="button"
                        onClick={() => setSelectedDay(key)}
                        data-testid={`cal-day-${key}`}
                        style={{
                          minHeight: 100,
                          padding: 8,
                          textAlign: "left",
                          background: isToday ? "rgba(31,182,193,0.10)" : "#FFFFFF",
                          border: selectedDay === key
                            ? "2px solid #1FB6C1"
                            : "1px solid rgba(12,42,64,0.10)",
                          opacity: inMonth ? 1 : 0.45,
                          borderRadius: 8,
                          cursor: "pointer",
                        }}>
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ fontSize: "0.85rem",
                                   fontWeight: isToday ? 700 : 500 }}>
                      {date.getDate()}
                    </span>
                    {posts.length > 0 && (
                      <span className="font-mono"
                            style={{ fontSize: "0.6rem",
                                     color: "rgba(12,42,64,0.55)" }}>
                        {posts.length}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {posts.slice(0, 3).map((p) => (
                      <div key={p.id}
                           style={{ fontSize: "0.65rem",
                                    color: NETWORK_COLOR[p.angle] || "#0C2A40",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis" }}>
                        ● {(p.hook || p.source_title || "").slice(0, 22)}
                      </div>
                    ))}
                    {posts.length > 3 && (
                      <div style={{ fontSize: "0.6rem",
                                    color: "rgba(12,42,64,0.55)" }}>
                        +{posts.length - 3} more
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* selected day list */}
        {selectedDay && (
          <div className="hv-card-soft mt-4" style={{ padding: 16 }}
               data-testid={`cal-day-detail-${selectedDay}`}>
            <h3 className="mb-3"
                style={{ fontSize: "1rem", fontWeight: 600 }}>
              {new Date(selectedDay).toLocaleDateString("en-ZA",
                { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              {" "}— {(byDay[selectedDay] || []).length} post(s)
            </h3>
            {(byDay[selectedDay] || []).length === 0 && (
              <p className="font-light"
                 style={{ fontSize: "0.85rem",
                          color: "rgba(12,42,64,0.55)" }}>
                Nothing scheduled for this day.
              </p>
            )}
            <ul className="space-y-2">
              {(byDay[selectedDay] || []).map((p) => (
                <li key={p.id}
                    className="flex items-center justify-between gap-3 flex-wrap"
                    style={{ padding: "8px 10px",
                             background: "#FFFFFF",
                             border: "1px solid rgba(12,42,64,0.10)",
                             borderRadius: 8 }}
                    data-testid={`cal-post-${p.id}`}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span style={{ color: NETWORK_COLOR[p.angle] || "#0C2A40",
                                     fontWeight: 700 }}>●</span>
                      <span className="font-mono uppercase"
                            style={{ fontSize: "0.58rem",
                                     letterSpacing: "0.18em",
                                     color: NETWORK_COLOR[p.angle] || "#0C2A40" }}>
                        {p.angle}
                      </span>
                      <span className="font-mono"
                            style={{ fontSize: "0.62rem",
                                     color: "rgba(12,42,64,0.55)" }}>
                        {new Date(p.buffer_due_at || p.scheduled_for)
                          .toLocaleTimeString("en-ZA",
                            { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.88rem", fontWeight: 500,
                                  lineHeight: 1.3 }}>
                      {p.hook || p.source_title || "(untitled)"}
                    </div>
                  </div>
                  <a href={`/portal/content-calendar/plan/${p.source_type || "compose"}/${p.source_slug}`}
                     target="_blank" rel="noreferrer"
                     className="hv-pill"
                     style={{ textDecoration: "none" }}
                     data-testid={`cal-post-open-${p.id}`}>
                    <ExternalLink size={11} /> Open
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {loading && (
          <div className="mt-3 font-mono uppercase"
               style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                        color: "rgba(12,42,64,0.55)" }}>Loading…</div>
        )}
      </div>
    </ContentPlannerLayout>
  );
}
